import { QuickAddComponent } from '@theme/quick-add';

class FeaturedRingCarousel extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.track = this.querySelector('.frc__track');
    if (!this.track) return;

    const signal = this.abortController.signal;
    this.track.addEventListener('scroll', () => this.scheduleNavigation(), { passive: true, signal });
    this.track.addEventListener('keydown', (event) => {
      if (event.target !== this.track) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }
    }, { signal });
    this.addEventListener('click', (event) => this.handleClick(event), { capture: true, signal });
    this.addEventListener('change', (event) => this.selectColor(event), { signal });
    this.resizeObserver = new ResizeObserver(() => this.scheduleNavigation());
    this.resizeObserver.observe(this.track);
    this.scheduleNavigation();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.navigationFrame);
  }

  scheduleNavigation() {
    cancelAnimationFrame(this.navigationFrame);
    this.navigationFrame = requestAnimationFrame(() => {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const position = Math.abs(this.track.scrollLeft);
      const previous = this.querySelector('[data-frc-direction="-1"]');
      const next = this.querySelector('[data-frc-direction="1"]');
      if (previous) previous.hidden = position <= 2 || max <= 2;
      if (next) next.hidden = position >= max - 2 || max <= 2;
    });
  }

  move(direction) {
    const slide = this.track.querySelector('.frc__slide');
    if (!slide) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollBy({
      left: (slide.getBoundingClientRect().width + gap) * direction * rtl,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  }

  async handleClick(event) {
    if (!(event.target instanceof Element)) return;
    const navigation = event.target.closest('[data-frc-direction]');
    if (navigation) {
      this.move(Number(navigation.dataset.frcDirection));
      return;
    }

    const button = event.target.closest('.quick-add__button--choose');
    if (!button || !this.contains(button)) return;
    const quickAdd = button.closest('quick-add-component');
    if (!(quickAdd instanceof QuickAddComponent)) return;
    event.preventDefault();
    event.stopPropagation();
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const opened = await quickAdd.handleClick(event);
      if (opened === false && quickAdd.productPageUrl) window.location.assign(quickAdd.productPageUrl);
    } catch {
      const link = quickAdd.closest('product-card')?.querySelector('[ref="productCardLink"]');
      if (link?.href) window.location.assign(link.href);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  selectColor(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('.frc-card__swatch input')) return;
    const card = input.closest('.frc-card');
    if (!card) return;

    card.querySelectorAll('[data-frc-product-link]').forEach((link) => {
      link.href = input.dataset.productUrl;
    });

    if (input.dataset.imageSrc) {
      let image = card.querySelector('.frc-card__image');
      if (!image) {
        image = document.createElement('img');
        image.className = 'frc-card__image';
        image.sizes = '(min-width: 1100px) 20vw, (min-width: 750px) 33vw, 52vw';
        image.alt = card.querySelector('.frc-card__title').textContent.trim();
        card.querySelector('.frc-card__media').replaceChildren(image);
      }
      image.srcset = input.dataset.imageSrcset || '';
      image.src = input.dataset.imageSrc;
    }

    const price = card.querySelector('[data-frc-price]');
    if (price) price.textContent = input.dataset.formattedPrice;
    card.querySelector('.frc-card__from')?.remove();

    const savings = card.querySelector('.frc-card__savings');
    if (savings) {
      savings.textContent = input.dataset.savingsLabel;
      savings.hidden = Number(input.dataset.savings) <= 0;
      card.classList.toggle('frc-card--sale', !savings.hidden);
    }

    const variantInput = card.querySelector('product-form-component input[name="id"]');
    if (variantInput) variantInput.value = input.dataset.variantId;
  }
}

if (!customElements.get('featured-ring-carousel')) {
  customElements.define('featured-ring-carousel', FeaturedRingCarousel);
}
